// src/components/PortfolioComment.jsx
// 종합 방향성 코멘트(제안형) — 섹터 편향/집중 · 롱숏 상관 · 넷 노출/베타 기반.
import React, { useEffect, useMemo, useState } from "react";
import { portfolioExposure } from "../lib/exposure";
import { sectorOf } from "../lib/sectors";
import { buildComments, SECTOR_ETF, SECTOR_ETF_CODES, MASTER_SECTORS } from "../lib/portfolioComment";
import SectorLeadersModal from "./SectorLeadersModal";
import TechnicalMonitor from "./TechnicalMonitor";
import { SECTOR_LEADERS } from "../lib/sectorLeaders";
import { fetchMomo, fetchRegime, fetchMomoStocks, fetchSectorTheses, fetchFeatureStocks } from "../hooks/useSheets";
import { SECTOR_THESES } from "../lib/sectorTheses";

const bareCode = (t) => String(t == null ? "" : t).replace(/\D/g, "").replace(/^0+/, "");
const code6 = (t) => String(t == null ? "" : t).replace(/\D/g, "").padStart(6, "0");

// 테마(반도체/2차전지/원전/로봇/우주) 종목코드 → 테마. "내 편입" 팝업을 테마 인식시키기 위함(대표주 기준).
const THEME_BY_CODE = {};
["반도체", "2차전지", "원전", "로봇", "우주"].forEach((sec) => {
  (SECTOR_LEADERS[sec] || []).forEach((s) => { THEME_BY_CODE[s.c] = sec; });
});
const effSector = (ticker, market) => THEME_BY_CODE[code6(ticker)] || sectorOf(ticker, market);


export default function PortfolioComment({ holdings, marketData, exchangeRates, nav, corr }) {
  const [momo, setMomo] = useState(null);
  const [regime, setRegime] = useState(null);
  const [momoStocks, setMomoStocks] = useState({});
  const [flows, setFlows] = useState({});   // 6자리코드 → {intensity5, foreignRatio}
  const [theses, setTheses] = useState(SECTOR_THESES);   // 백엔드(자동) + 번들 시드 병합
  const [features, setFeatures] = useState({ asof: null, stocks: [] });   // kr_daily 특징주

  useEffect(() => {
    let alive = true;
    fetchMomo().then((j) => { if (alive) setMomo(j.momo || null); }).catch(() => {});
    fetchRegime().then((j) => { if (alive && j && j.regime) setRegime(j); }).catch(() => {});
    fetchMomoStocks().then((j) => {
      if (!alive) return;
      const norm = {};
      Object.keys(j.stocks || {}).forEach((k) => { norm[bareCode(k)] = j.stocks[k]; });
      setMomoStocks(norm);
    }).catch(() => {});
    fetchSectorTheses().then((j) => {
      if (!alive || !j || !j.theses) return;
      // 백엔드가 시드 위에 덮어쓰되, 논거(summary) 없는 섹터(예: 유틸리티=텔레그램만)는 시드 논거를 보존.
      const merged = { ...SECTOR_THESES };
      Object.entries(j.theses).forEach(([k, v]) => {
        const base = SECTOR_THESES[k] || {};
        merged[k] = { ...base, ...v };
        if (!v.summary && base.summary) {   // 백엔드에 논거 없음 → 시드 논거 유지 + 캘린더는 시드+텔레그램 합집합
          merged[k].summary = base.summary;
          merged[k].tag = base.tag;
          merged[k].drivers = base.drivers;
          const seen = new Set();
          merged[k].catalysts = [...(base.catalysts || []), ...(v.catalysts || [])]
            .filter((c) => { const key = `${c.date}|${c.text}`; if (seen.has(key)) return false; seen.add(key); return true; });
        }
      });
      setTheses(merged);
    }).catch(() => {});
    fetchFeatureStocks().then((j) => { if (alive && j) setFeatures({ asof: j.asof, stocks: j.stocks || [] }); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // 투자자 수급(네이버) — KR 보유종목 + 섹터 ETF 코드로 조회
  const codesKey = useMemo(() => {
    const held = (holdings || [])
      .filter((h) => ["KR", "KS", "KQ"].includes(String(h.market || "").toUpperCase()) && code6(h.ticker).replace(/0/g, ""))
      .map((h) => code6(h.ticker));
    return [...new Set([...held, ...SECTOR_ETF_CODES])].sort().join(",");
  }, [holdings]);

  // WICS 섹터 → ETF 신호(모멘텀·수급) 맵
  const sectorSignals = useMemo(() => {
    const out = {};
    Object.entries(SECTOR_ETF).forEach(([sector, code]) => { if (flows[code]) out[sector] = flows[code]; });
    return out;
  }, [flows]);
  useEffect(() => {
    if (!codesKey) { setFlows({}); return; }
    let alive = true;
    fetch(`/api/investor-flow?codes=${encodeURIComponent(codesKey)}`)
      .then((r) => r.json())
      .then((j) => { if (alive) setFlows(j.flows || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [codesKey]);

  // 섹터별 롱/숏 비중(그로스 대비 %)
  const sectorRows = useMemo(() => {
    if (!holdings?.length) return [];
    const { rows, grossKRW } = portfolioExposure(holdings, marketData, exchangeRates);
    if (!grossKRW) return [];
    const by = {};
    rows.filter((r) => r.hasPrice).forEach((r) => {
      const sec = sectorOf(r.ticker, r.market);
      const b = by[sec] || (by[sec] = { longKRW: 0, shortKRW: 0 });
      if (r.short) b.shortKRW += Math.abs(r.signedKRW); else b.longKRW += Math.abs(r.signedKRW);
    });
    return Object.entries(by).map(([sector, v]) => ({
      sector, longPct: (v.longKRW / grossKRW) * 100, shortPct: (v.shortKRW / grossKRW) * 100,
    }));
  }, [holdings, marketData, exchangeRates]);

  // 넷 노출은 실시간 보유로 계산한다. momo.net은 로컬 push_momo.py가 올리는 어제자
  // 엑셀 스냅샷이라, 오늘 포지션을 바꾸면 코멘트만 옛 수치를 말한다(2026-08-20:
  // 실제 넷 0.9%인데 코멘트는 +8.0%라고 했다). 베타는 종목별 베타가 없어 momo 유지.
  const liveNetPct = useMemo(() => {
    if (!holdings?.length || !nav) return null;
    const { netKRW } = portfolioExposure(holdings, marketData, exchangeRates);
    return (netKRW / nav) * 100;
  }, [holdings, marketData, exchangeRates, nav]);

  // 종목별 포지션(그로스 비중 + STM/MTM) — 크라우딩/리버전 분석용
  const positions = useMemo(() => {
    if (!holdings?.length) return [];
    const { rows, grossKRW } = portfolioExposure(holdings, marketData, exchangeRates);
    if (!grossKRW) return [];
    return rows.filter((r) => r.hasPrice).map((r) => {
      const m = momoStocks[bareCode(r.ticker)] || {};
      const fl = flows[code6(r.ticker)];
      return { short: r.short, sector: sectorOf(r.ticker, r.market), w: Math.abs(r.signedKRW) / grossKRW, stm: m.stm, mtm: m.mtm, flow: fl?.intensity5 };
    });
  }, [holdings, marketData, exchangeRates, momoStocks, flows]);

  // 섹터별 내 편입 종목 (대표주 팝업 오른쪽에 표시)
  const myBySector = useMemo(() => {
    if (!holdings?.length) return {};
    const { rows, grossKRW } = portfolioExposure(holdings, marketData, exchangeRates);
    const out = {};
    rows.forEach((r) => {
      const sec = effSector(r.ticker, r.market);
      (out[sec] = out[sec] || []).push({
        name: r.name || String(r.ticker), ticker: r.ticker, short: r.short,
        wPct: grossKRW ? (Math.abs(r.signedKRW) / grossKRW) * 100 : null,
      });
    });
    return out;
  }, [holdings, marketData, exchangeRates]);

  const { headline, points, rotationTargets } = useMemo(
    () => buildComments({ positions, sectorRows, momo, corr, regime, sectorSignals, liveNetPct }),
    [positions, sectorRows, momo, corr, regime, sectorSignals, liveNetPct]
  );
  const [pickSector, setPickSector] = useState(null);
  const [candNews, setCandNews] = useState({});   // 편입후보 코드 → {headline, url} (이유)

  // 편입 후보 — 오늘 특징주(gainer/순매수) 중 내가 얇은(롱<10%) 섹터에 맞고 미보유·우호적인 종목
  const candidates = useMemo(() => {
    const stocks = features.stocks || [];
    if (!stocks.length) return [];
    const held = new Set((holdings || []).map((h) => bareCode(h.ticker)));
    const longTot = sectorRows.reduce((s, r) => s + r.longPct, 0) || 1;
    const share = {};
    sectorRows.forEach((r) => { share[r.sector] = r.longPct / longTot; });   // 롱 대비 섹터 비중
    return stocks
      .filter((s) => {
        const sec = sectorOf(s.c, "KR");
        const favorable = (s.pct || 0) > 0 || ((s.frgn || 0) + (s.inst || 0)) > 0;
        return !held.has(bareCode(s.c)) && (share[sec] || 0) < 0.10 && favorable;
      })
      .map((s) => ({ ...s, sector: sectorOf(s.c, "KR") }))
      .slice(0, 6);
  }, [features, holdings, sectorRows]);

  // 편입 후보의 이유 — 네이버 종목뉴스 헤드라인(종목명 포함 우선)
  const candKey = candidates.map((c) => c.c).join(",");
  useEffect(() => {
    if (!candidates.length) { setCandNews({}); return; }
    const nameByCode = {};
    candidates.forEach((c) => { nameByCode[String(c.c)] = c.n; });
    let alive = true;
    fetch(`/api/news?symbols=${encodeURIComponent(candidates.map((c) => `${c.c}:KR`).join(","))}`)
      .then((r) => r.text())
      .then((text) => {
        const map = {};
        text.split("\n").filter(Boolean).forEach((line) => {
          try {
            const d = JSON.parse(line);
            const nm = nameByCode[String(d.ticker)];
            const items = d.news || [];
            const hit = items.find((n) => nm && (n.headline || "").includes(nm)) || items[0];
            if (hit) map[String(d.ticker)] = { headline: hit.headline, url: hit.url };
          } catch { /* skip */ }
        });
        if (alive) setCandNews(map);
      }).catch(() => {});
    return () => { alive = false; };
  }, [candKey]);

  // 2단 섹터: 로테이션 후보(상단) = 빈 섹터 + 일정임박(≤7일)/상대강도(mom≥+3%) 승격. 나머지 15섹터 = 관심 섹터.
  const { promoted, watch } = useMemo(() => {
    const near = (sec) => (theses[sec]?.catalysts || []).some((c) => {
      const t = Date.parse(c.date);
      return t && t >= Date.now() && t - Date.now() <= 7 * 86400000;
    });
    const rotMom = {};
    (rotationTargets || []).forEach((r) => { rotMom[r.sector] = r.mom; });
    const promo = new Set((rotationTargets || []).map((r) => r.sector));   // 빈 섹터(기존 로직)
    MASTER_SECTORS.forEach((sec) => { if (near(sec)) promo.add(sec); });    // ⏰ 일정 임박(≤7일)만
    // 상대강도: 모멘텀 1등 섹터 하나만 승격(절대임계값 쓰면 상승장에 다 올라옴)
    const ranked = MASTER_SECTORS
      .map((s) => ({ s, mom: sectorSignals[s]?.mom }))
      .filter((x) => x.mom != null)
      .sort((a, b) => b.mom - a.mom);
    if (ranked[0] && ranked[0].mom > 0) promo.add(ranked[0].s);
    const mk = (sec) => ({ sector: sec, mom: rotMom[sec] ?? sectorSignals[sec]?.mom ?? null, near: near(sec) });
    const rotSecs = (rotationTargets || []).map((r) => r.sector);          // 빈 섹터 먼저(랭킹순)
    const extra = MASTER_SECTORS.filter((s) => promo.has(s) && !rotSecs.includes(s));
    return {
      promoted: [...rotSecs, ...extra].map(mk),
      watch: MASTER_SECTORS.filter((s) => !promo.has(s)).map(mk),
    };
  }, [rotationTargets, theses, sectorSignals]);

  if (!sectorRows.length) return null;

  const chip = (r, muted) => {
    const tgFresh = theses[r.sector]?.tgAsof && (Date.now() - new Date(theses[r.sector].tgAsof).getTime()) < 3 * 86400000;
    const bg = tgFresh ? "#fdf6e3" : muted ? "#f4f5f8" : "#eef3fb";
    return (
      <button key={r.sector} onClick={() => setPickSector(r.sector)}
        style={{ border: `1px solid ${muted ? "#e2e5ec" : "#cdd7e6"}`, background: bg, color: muted ? "#667085" : "#1f3a93", borderRadius: 999, padding: "3px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
        {tgFresh && <span title="새 텔레그램 내용" style={{ marginRight: 3 }}>🟡</span>}
        {r.near && <span title="일정 임박(≤7일)" style={{ marginRight: 2 }}>⏰</span>}
        {r.sector}
        {r.mom != null && <span style={{ fontWeight: 500, color: muted ? "#98a2b3" : "#6a7fa0", marginLeft: 4 }}>{r.mom >= 0 ? "+" : ""}{(r.mom * 100).toFixed(0)}%</span>}
      </button>
    );
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>종합 코멘트 (전략 제안)</h3>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          레짐 · 넷/베타 · 롱숏 헤지 · 섹터 쏠림 종합 · 판단 보조용(결정은 직접)
        </div>
      </div>

      {/* 헤드라인 */}
      <div style={{ background: "#1a1a2e", color: "#fff", borderRadius: 10, padding: "13px 16px", fontSize: 13.5, fontWeight: 700, lineHeight: 1.5, marginBottom: 10 }}>
        {headline}
      </div>

      {/* 진단·제안 — 한 박스, 앞 아이콘은 작은 색점(레벨)으로 */}
      <div style={{ background: "#fff", border: "1px solid #ececf2", borderRadius: 10 }}>
        {points.map((c, i) => {
          // 문제없음=초록 / 주의(행동 제안 문구)=노랑 / 위험=빨강
          const caution = c.level === "info" && /(고려|검토|신중|점검|취약|재검토|축소|보강|늘려|편승|이탈|확인 후|진입하세요|재검토하세요)/.test(c.text);
          const dot = c.level === "warn" ? "#c0392b" : caution ? "#e0a800" : "#0a7c59";
          return (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 14px", borderTop: i === 0 ? "none" : "1px solid #f4f4f7" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, marginTop: 6, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#333", lineHeight: 1.5 }}>{c.text}</span>
            </div>
          );
        })}
      </div>

      {/* 섹터 로테이션 — 로테이션 후보(승격) + 편입 후보 좌우반반, 아래 관심 섹터 watchlist */}
      <div style={{ marginTop: 12, background: "#fbfbfd", border: "1px solid #e8e8ee", borderRadius: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {/* 로테이션 후보 (빈 섹터 + 일정임박/상대강도 승격) */}
            <div style={{ padding: "12px 16px", borderRight: "1px solid #eef0f4", minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#1f3a93", marginBottom: 8 }}>
                🔄 로테이션 후보 <span style={{ fontWeight: 500, color: "#98a2b3" }}>(빈 섹터 + ⏰일정·상대강도 승격)</span>
              </div>
              {promoted.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {promoted.map((r) => chip(r, false))}
                </div>
              ) : <span style={{ fontSize: 12, color: "#bbb" }}>해당 없음</span>}
            </div>
            {/* 편입 후보 (오늘 특징주 × 빈 섹터) */}
            <div style={{ padding: "12px 16px", minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#b8860b", marginBottom: 8 }}>
                🎯 편입 후보 <span style={{ fontWeight: 500, color: "#bfa76a" }}>(오늘 특징주 × 빈 섹터{features.asof ? ` · ${String(features.asof).slice(0, 10)}` : ""})</span>
              </div>
              {candidates.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {candidates.map((s) => {
                    const reason = candNews[String(s.c)];
                    return (
                      <div key={s.c} style={{ display: "grid", gridTemplateColumns: "minmax(110px, auto) 1fr", gap: 10, alignItems: "baseline" }}>
                        <div style={{ fontSize: 12.5, color: "#333", whiteSpace: "nowrap" }}>
                          <span style={{ fontSize: 10, color: "#888", marginRight: 5 }}>[{s.sector}]</span>
                          <b>{s.n}</b>
                          {s.tag === "gainer" && s.pct != null && <span style={{ color: "#c0392b", marginLeft: 5 }}>+{s.pct}%↑</span>}
                          {((s.frgn || 0) + (s.inst || 0)) > 0 && <span style={{ color: "#0a7c59", marginLeft: 5 }}>순매수 {((s.frgn || 0) + (s.inst || 0)).toLocaleString()}억</span>}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#777", minWidth: 0, lineHeight: 1.4 }}>
                          {reason
                            ? <a href={reason.url} target="_blank" rel="noopener noreferrer" style={{ color: "#777", textDecoration: "none" }}>💬 {reason.headline}</a>
                            : <span style={{ color: "#c9bfa2" }}>—</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <span style={{ fontSize: 12, color: "#bbb" }}>해당 없음</span>}
            </div>
          </div>
          {/* 관심 섹터 watchlist — 전체 15섹터(승격되면 위 로테이션 후보로 이동) */}
          {watch.length > 0 && (
            <div style={{ padding: "10px 16px 12px", borderTop: "1px solid #eef0f4" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#667085", marginBottom: 8 }}>
                👀 관심 섹터 <span style={{ fontWeight: 500, color: "#98a2b3" }}>(누르면 대표주·텔레그램 · 🟡새글 · ⏰일정임박 시 위로 승격)</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {watch.map((r) => chip(r, true))}
              </div>
            </div>
          )}
        </div>

      {pickSector && (
        <SectorLeadersModal sector={pickSector} signal={sectorSignals[pickSector]} thesis={theses[pickSector]} myHoldings={myBySector[pickSector] || []} onClose={() => setPickSector(null)} />
      )}

      {/* 보유종목 기술적 신호 모니터 (엔벨로프·이평선·채널·추세선) */}
      <TechnicalMonitor holdings={holdings} />
    </div>
  );
}
